import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RelationshipBadge } from '../components/StatusBadge';
import { partnerGateway } from '../services';
import { useLoad } from '../state/useLoad';
import { usePartner } from '../state/usePartner';
import { RELATIONSHIP_STATUS_LABELS, type PartnerClient, type RelationshipStatus } from '../types';

const PENDING: RelationshipStatus[] = ['InvitationSent', 'AwaitingClientAcceptance'];

export function PartnerClientsPage() {
  const { membership, can } = usePartner();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const status = params.get('status') ?? '';
  const { data, error, loading } = useLoad((signal) => partnerGateway.listClients(membership.partnerOrganizationId, signal), membership.partnerOrganizationId, 'Your clients could not be loaded.');

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data ?? []).filter((c: PartnerClient) => {
      const matchesStatus = !status || (status === 'pending' ? PENDING.includes(c.status) : c.status === status);
      return matchesStatus && (!term || (c.clientCompanyName ?? c.clientName).toLowerCase().includes(term));
    });
  }, [data, status, search]);

  const canAdd = can('partner.clients.manage') && membership.status === 'Active';

  return (
    <>
      <div className="pp-page-head">
        <h1>Clients</h1>
        {canAdd && <Link className="pp-button pp-button--primary" to="/partner/clients/new">Add Client</Link>}
      </div>

      <div className="pp-filters">
        <label>
          Search
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client name" />
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setParams(e.target.value ? { status: e.target.value } : {})}>
            <option value="">All</option>
            <option value="pending">Awaiting client (any invitation)</option>
            {(Object.keys(RELATIONSHIP_STATUS_LABELS) as RelationshipStatus[]).filter((s) => s !== 'Prospect').map((s) => (
              <option key={s} value={s}>{RELATIONSHIP_STATUS_LABELS[s]}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="pp-error">{error}</p>}
      {loading && !data && <p className="pp-muted">Loading…</p>}
      {data && rows.length === 0 && <p className="pp-muted">{data.length === 0 ? 'You have no clients yet.' : 'No clients match.'}</p>}

      {rows.length > 0 && (
        <div className="pp-table-wrap">
          <table className="pp-table">
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">Status</th>
                <th scope="col">Services</th>
                <th scope="col">Assigned</th>
                <th scope="col">Access ends</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <th scope="row"><Link to={`/partner/clients/${c.id}`}>{c.clientCompanyName ?? c.clientName}</Link></th>
                  <td><RelationshipBadge status={c.status} /></td>
                  <td>{(c.grantedServices.length > 0 ? c.grantedServices : c.requestedServices).length}</td>
                  <td>{c.assignedMemberCount}</td>
                  <td>{c.accessExpiresAtUtc ? new Date(c.accessExpiresAtUtc).toLocaleDateString() : 'No end date'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
